import { isEmpty } from 'lodash';
import { ChangeEvent, DragEvent, useState } from 'react';
import { FieldValues, UseFormSetValue } from 'react-hook-form';
import { Snackbar } from 'src/components/snackbar';
import { SUPPORTED_EXTENSIONS } from 'src/constants';
import { useGetAllAssets, useUploadAsset } from 'src/hooks';
import { IAsset } from 'src/interfaces';
import { lang } from 'src/utils';

export const useResourceSelectorSidebarHeader = (props: {
  onCloseModal?: () => void;
  allowedExtensions?: string[];
  setFieldValue?: UseFormSetValue<FieldValues>;
  onSelectAsset?: (file: File) => void;
  validateFile?: (file: IAsset | File) => boolean;
}) => {
  const uploadNewAsset = useUploadAsset();
  const [isDragging, setIsDragging] = useState(false);
  const [overWriteAssetModal, setOverWriteAssetModal] = useState({
    open: false,
    fileToOverride: null as File | null,
  });
  const { data: assets = [], isLoading: isLoadingAssets } = useGetAllAssets();

  const uploadAsset = async (file: File) => {
    try {
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
      const extensionsList = props.allowedExtensions || SUPPORTED_EXTENSIONS;

      if (!isEmpty(fileExtension) && extensionsList.includes(fileExtension)) {
        await uploadNewAsset.mutateAsync(file); // Upload the asset
        props.setFieldValue && props.setFieldValue('uploadedAsset', file);
        props.setFieldValue &&
          props.setFieldValue('customizedOutageConfig.stamp.url', file.name, {
            shouldDirty: true,
          });
        props.onSelectAsset && props.onSelectAsset(file);
        Snackbar.success(
          `${lang.file} ${file.name} ${lang.successfullyUploaded}`,
        );
        props.onCloseModal?.();
      } else {
        Snackbar.error(lang.failedToUploadExtension(fileExtension));
      }
    } catch (error: any) {
      Snackbar.error(error?.message);
    }
  };
  const isDuplicateAsset = (uploadedFileName: string) => {
    return assets.some((asset) => asset.fileName === uploadedFileName);
  };
  const handleUploadAsset = async (file: File) => {
    const uploadedFileName = file.name;

    if (!isLoadingAssets && isDuplicateAsset(uploadedFileName)) {
      setOverWriteAssetModal({
        open: true,
        fileToOverride: file,
      });

      return;
    }

    await uploadAsset(file);
  };
  const handleOverrideAsset = async () => {
    if (overWriteAssetModal.fileToOverride) {
      const fileToOverride = overWriteAssetModal.fileToOverride;
      if (props.validateFile && !props.validateFile(fileToOverride)) {
        setOverWriteAssetModal({ open: false, fileToOverride: null });

        return;
      }
      await uploadAsset(fileToOverride);
      setOverWriteAssetModal({ open: false, fileToOverride: null });
    }
  };

  const handleCancelOverride = () => {
    setOverWriteAssetModal({ open: false, fileToOverride: null });
  };
  const handleFileInput = async (e: ChangeEvent<HTMLInputElement>) => {
    const fileInput = e.target;

    if (fileInput.files && fileInput.files[0]) {
      const file = fileInput.files[0];

      fileInput.value = '';

      if (file) {
        await handleUploadAsset(file);
      } else {
        Snackbar.error(lang.failedToUpload);
      }
    }
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files) {
      const files = e.dataTransfer.files;

      if (files.length > 1) {
        Snackbar.error(lang.failedToUploadFile);

        return;
      }

      const file = files[0];
      if (file) {
        await handleUploadAsset(file);
      } else {
        Snackbar.error(lang.failedToUpload);
      }
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  return {
    uploadNewAsset,
    handleFileInput,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    isDragging,
    overWriteAssetModal,
    handleOverrideAsset,
    handleCancelOverride,
  };
};
